import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import striptags from 'striptags';
import cx from 'classnames';

import WelcomePage from '../common/welcomePage';
import ProjectItemOrPage from './projectItemOrPage';

import { PAGE_TYPES } from '../types/survey';
import { isConceptTest, isPageTitle, isQuestionLoop } from '../utils/helpers';
import { ITEM_TYPES } from '../types/objectTypes';
import { Features, isFeatureEnabled } from '../../featureToggles/features';

export class Page extends Component {
  constructor(props) {
    super(props);
    this.onChange = this.onChange.bind(this);
    this.container = React.createRef();
  }

  componentDidMount() {
    const { resetScroll } = this.props;
    if (resetScroll) {
      resetScroll(this.container.current.offsetHeight);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentPage !== prevProps.currentPage) {
      this.props.resetScroll(this.container.current.offsetHeight);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(nextState, this.state) || !isEqual(nextProps, this.props);
  }

  onChange(frontendId, value, subtype) {
    this.props.onChange(frontendId, value, subtype);
  }

  renderWelcomePage() {
    const { welcomeText, termsAndConditions, uiTags, onAgreeChange } = this.props;
    return (
      <WelcomePage
        welcomeText={welcomeText}
        termsAndConditions={termsAndConditions}
        agreeChange={onAgreeChange}
        uiTags={uiTags}
      />
    );
  }

  renderPage() {
    const {
      isSurveyOver,
      uiTags: { genericThankYouMessageLabel },
      customization,
      multipleLanguages,
    } = this.props;
    if (isSurveyOver) {
      return (
        <div className={cx('submitMessage', { withLogo: !!customization.logo, withLanguages: multipleLanguages })}>
          {genericThankYouMessageLabel}
        </div>
      );
    }

    const { page } = this.props;

    if (!page) {
      return [];
    }

    return (
      <div key={page.frontendId}>
        {this.renderPageBreakTitle()}
        <div className="questions">{this.renderPageItems()}</div>
      </div>
    );
  }

  renderPageItems() {
    const {
      locked = false,
      page = {},
      pageItems = [],
      questionVisible,
      isCapture,
      pageShownTimes,
      isPrevPageEnabled,
      fetchingTranslations,
      selectedLanguage,
      catalog,
      isViewAnswers,
      responses,
    } = this.props;
    const { shouldHideConceptTestDecorator = false } = page;
    const validPageItems = pageItems.filter(
      (item) => item && !item.isEmbeddedVariable && !item.isHiddenByUrlVariable && !item.config.hideOnSurvey
    );
    const firstItem = validPageItems[0] || {};
    const pageIsConceptTest = isConceptTest(firstItem);
    const validPageItemsWithoutDecorators = validPageItems.filter((item) => item.type !== ITEM_TYPES.decorator);
    const firstValidItemIndex = isQuestionLoop(firstItem) || firstItem.isEmbeddedVariable ? 1 : 0;
    let questionCounter = 0;
    return validPageItems
      .map((item, index) => {
        if (item.isEmbeddedVariable) {
          return null;
        }
        if (item.isHiddenByUrlVariable) {
          return null;
        }
        let questionIsVisible = true;
        if (pageIsConceptTest && !isViewAnswers) {
          questionIsVisible = !!(index === 0 || questionVisible[item.pageFrontendId]);
        }
        let questionNumber = item.questionNumberForSurvey;
        if (isViewAnswers && responses[item.frontendId]) {
          questionCounter++;
          questionNumber = questionCounter;
        }
        return (
          <ProjectItemOrPage
            selectedLanguage={selectedLanguage}
            fetchingTranslations={fetchingTranslations}
            isCapture={isCapture}
            key={`${item._id}_${pageShownTimes}`}
            loopedPage={!!page.loopingEnabled}
            items={this.props.items}
            itemsIndexed={this.props.itemsIndexed}
            rows={this.props.rows}
            options={this.props.options}
            item={item}
            index={questionNumber}
            customization={this.props.customization}
            isVisible
            questionVisible={questionIsVisible}
            onChange={this.props.onChange}
            shouldHideConceptTestDecorator={shouldHideConceptTestDecorator}
            decoratorsOnly={this.props.decoratorsOnly}
            locked={locked}
            prevPage={this.props.prevPage}
            noPrevPage={!isPrevPageEnabled}
            nextPage={this.props.nextPage}
            isFirstItem={index === firstValidItemIndex}
            isLastItem={index === validPageItems.length - 1}
            isOnlyItemOnPage={validPageItemsWithoutDecorators.length === 1}
            catalog={catalog}
            itemIndex={index}
          />
        );
      })
      .filter((item) => !!item);
  }

  renderPageBreakTitle() {
    const { pageTitle, page = {}, itemsTranslations } = this.props;
    if (!pageTitle || page.isConceptTest) {
      return null;
    }
    const translation = itemsTranslations[`page_${pageTitle.frontendId}`] || {};
    const translatedTitle = translation.title || '';
    const title = translatedTitle || pageTitle.title;

    const isPageBreakTitleAvailable = !!title && !!striptags(title).length;
    if (!isPageBreakTitleAvailable) {
      return null;
    }

    return <div className="title" dangerouslySetInnerHTML={{ __html: title }} data-e2e-selector="page-title" />;
  }

  render() {
    const { page } = this.props;
    let pageToRender;
    if (page.type === PAGE_TYPES.WELCOME_PAGE) {
      pageToRender = this.renderWelcomePage();
    } else {
      pageToRender = this.renderPage();
    }
    return (
      <div className="page" ref={this.container}>
        {pageToRender}
      </div>
    );
  }
}

Page.propTypes = {
  survey: PropTypes.object.isRequired,
};

const mapStateToProps = (state, props) => {
  const {
    customization,
    activeProject: {
      project = {},
      survey = { breadcrumbs: [] },
      options = {},
      rows = {},
      items: itemsIndexed = {},
      questionVisible = {},
      translations: { data: translations = {}, fetching: fetchingTranslations } = {},
      pages = [],
    } = {},
    capture: { selectedLanguage = 'en', availableLanguages = [], availableLocales = [] } = {},
    catalog: { items: catalog },
    pageShownCounters = {},
    visualizationTypes: { isViewAnswers = false } = {},
    responses,
  } = state;

  const { currentPage, isSurveyOver, pageToShow } = survey;
  const page = pages[pageToShow] || {};
  const { items: itemsFrontendIds = [] } = page;
  const items = itemsFrontendIds.map((itemFrontendId) => itemsIndexed[itemFrontendId]);

  const translation = translations[selectedLanguage] || {};
  const { uiTags = {}, welcome = {}, items: itemsTranslations = {} } = translation;

  let { welcomeText, termsAndConditions, fullItems } = page;
  if (welcome.greetings) {
    welcomeText = welcome.greetings;
  }
  if (welcome.termsAndConditions) {
    termsAndConditions = welcome.termsAndConditions;
  }

  const pageItems = page.items ? page.items.map((frontendId) => itemsIndexed[frontendId]) : [];
  let pageTitle;
  if (pageItems.length > 0 && isPageTitle(pageItems[0])) {
    pageTitle = pageItems[0];
    pageItems.splice(0, 1);
  }

  return {
    selectedLanguage,
    fetchingTranslations,
    customization,
    welcomeText,
    termsAndConditions,
    uiTags,
    itemsTranslations,
    survey,
    page,
    pageShownTimes: pageShownCounters[page.loopingStartingPage] || 0,
    isSurveyOver,
    currentPage,
    project,
    items,
    options,
    rows,
    translation,
    questionVisible,
    itemsIndexed,
    pageItems: isViewAnswers ? fullItems : pageItems,
    catalog,
    pageTitle,
    multipleLanguages: isFeatureEnabled(Features.panel) ? availableLocales.length > 0 : availableLanguages.length > 0,
    isViewAnswers,
    responses,
  };
};

const mapDispatchToProps = {};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Page));
